import { CollectionUtils, RecordUtils } from "@rsm-hcd/javascript-core";
import { Record } from "immutable";
import { Conversation } from "models/interfaces/conversations/conversation";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { ConversationWithoutMessages } from "utilities/atoms/conversations/conversation-history/conversation-history-atom-reducer";
import ConversationMessageFeedbackRecord from "./conversation-message-feedback-record";
import { ConversationMessageStatus } from "./conversation-message-status";

const defaultValues: Conversation = {
    id: undefined,
    title: undefined,
    createdAt: undefined,
    messages: [],
    isPinned: false,
};

export default class ConversationRecord
    extends Record(defaultValues)
    implements Conversation
{
    // Do NOT set properties on immutable records due to babel and typescript transpilation issue
    // See https://github.com/facebook/create-react-app/issues/6506

    // -----------------------------------------------------------------------------------------
    // #region Constructor
    // -----------------------------------------------------------------------------------------

    constructor(params?: Partial<Conversation>) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        if (CollectionUtils.hasValues(params.messages)) {
            params.messages = RecordUtils.ensureRecords(
                params.messages,
                ConversationMessageRecord
            );
        }

        super(params);
    }

    // #endregion Constructor

    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    updateMessage(message: ConversationMessageRecord): ConversationRecord {
        const foundMessageIdx = this.messages?.findIndex(
            (msg) => msg.id === message.id
        );

        const messages =
            foundMessageIdx == null ||
            foundMessageIdx < 0 ||
            this.messages == null ||
            this.messages.length === 0
                ? [...(this.messages || []), message]
                : CollectionUtils.replaceElementAt(
                      this.messages,
                      foundMessageIdx,
                      message
                  );

        return this.with({ messages });
    }

    updateMessageFeedback(
        feedback: ConversationMessageFeedbackRecord
    ): ConversationRecord {
        const foundMessageIdx = this.messages?.findIndex(
            (msg) => msg.id === feedback.messageId
        );

        const message = this.messages
            ?.find((msg) => msg.id === feedback.messageId)
            ?.with({ feedback, status: ConversationMessageStatus.Completed });
        if (
            message === undefined ||
            foundMessageIdx < 0 ||
            foundMessageIdx == null
        ) {
            return this;
        }

        const messages = CollectionUtils.replaceElementAt(
            this.messages,
            foundMessageIdx,
            message
        );

        return this.with({ messages });
    }
    public addMessage(message?: ConversationMessageRecord): ConversationRecord {
        if (message == null) {
            return this;
        }

        if (this.messages == null) {
            return this.with({ messages: [message] });
        }

        if (this.messages.some((msg) => msg.id === message.id)) {
            return this.updateMessage(message);
        }

        return this.with({
            messages: [...this.messages, message],
        });
    }

    public removeMessage(
        message?: ConversationMessageRecord
    ): ConversationRecord {
        if (message == null || this.messages == null) {
            return this;
        }

        const messageIndex = this.messages?.findIndex(
            (msg) => msg.id === message.id
        );

        return this.with({
            messages: CollectionUtils.removeElementAt(
                this.messages,
                messageIndex
            ),
        });
    }

    public removeMessages(): ConversationWithoutMessages {
        return this.remove("messages");
    }

    /**
     * Merges new values into the record and returns a new instance.
     *
     * @param {Partial<Conversation>} values
     */
    public with(values: Partial<Conversation>): ConversationRecord {
        return new ConversationRecord(Object.assign(this.toJS(), values));
    }

    // #endregion Public Methods
}
